/**
 * A diagram that lives in the database.
 *
 * The editor knows nothing about saving and the store knows nothing
 * about React; this is the join. It owns three things worth stating:
 *
 *  · WHEN TO SAVE. Debounced, so a drag across the screen is one write
 *    rather than two hundred, and skipped entirely when the document
 *    compares equal — every pointer move rebuilds the object, so a
 *    reference check would write continuously through a drag.
 *
 *  · WHAT A CONFLICT MEANS. The version moving on is not a failure to
 *    retry. Retrying is exactly the overwrite the version exists to
 *    prevent, so the save stops and the screen offers to reload.
 *
 *  · WHAT THE PERSON IS TOLD. "Saved", "Saving…", or the reason it did
 *    not — never nothing. A canvas that silently stopped saving is an
 *    hour of arranging lost with no moment where it could have been
 *    noticed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import DiagramEditor from './DiagramEditor.jsx';
import { Notice, Spinner } from '../canvasUi';
import { supabase } from '../../lib/supabase';
import { errorMessage } from '../../lib/supabaseErrorMessage';
import { useAuth } from '../../context/useAuth';
import {
  DIAGRAM_LOAD_TIMEOUT_MS, SAVE_AFTER_MS, docChanged, emptyDoc, loadDiagram, saveDiagram, withTimeout,
} from '../../lib/diagramStore';
import { seedFromRunsheet, seedSummary } from '../../lib/canvas/fromRunsheet';
import {
  documentFromChart, fetchLegacyChart, legacySummary,
} from '../../lib/canvas/fromLegacyChart';
import LucidImport from './LucidImport';
import useChartPresence from './useChartPresence';


export default function SavedDiagram({
  dealId = null,
  interestEstimateId = null,
  title = '',
  // The host opens a tagged document its own way and resolves false when
  // the document is gone.
  onOpenDocument = null,
  readOnly = false,
  // The runsheet this chain is drawn from. Given, the diagram can be
  // built rather than drawn — an agent who files a deed gets the box and
  // the arrow without touching the canvas, which is the difference
  // between this and a blank drawing tool.
  rows = null,
  land = '',
  // How this diagram reaches the database. Supplied, the two calls below
  // go through them instead of the Supabase client.
  //
  // This exists for ONE caller: the share link an outside title agent
  // opens, who has no session and so cannot be inside RLS. Everything
  // else about the editor — the debounce, the conflict rule, what the
  // person is told — is the same work whoever is drawing, and copying
  // the component to change two lines would have left two of them to
  // keep in step.
  loadDoc = null,
  saveDoc = null,
}) {
  const { profile } = useAuth();
  const [row, setRow] = useState(null);
  const [doc, setDoc] = useState(emptyDoc);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [notice, setNotice] = useState(null);
  // Bumped when a document arrives with something on it, and read by the
  // canvas as "fit this on screen once".
  //
  // It is minted HERE rather than worked out down in the canvas because
  // only the loader can tell a chart that came out of the database from
  // a chart somebody is drawing. The canvas sees shapes either way, so
  // fitting on "the doc became non-empty" would zoom the view to 2x
  // around the first box a person placed on a blank chart, as they
  // placed it. A chart that loads empty never gets a key.
  const [fitKey, setFitKey] = useState(null);
  // Read inside the loader without making it a dependency: a runsheet
  // that reloads must not re-seed a chart somebody has since emptied.
  // Kept current in an effect rather than assigned while rendering,
  // which React refuses.
  const latestRows = useRef(rows);
  useEffect(() => { latestRows.current = rows; }, [rows]);
  const [importing, setImporting] = useState(false);
  // The instrument behind a box, once somebody has asked for it. Fetched
  // on demand rather than loaded with the chart: a section chain is
  // sixty documents, and nobody opens sixty.

  /**
   * Everybody else in this chain, live.
   *
   * Anchored exactly as the diagram is, so the people who share a
   * channel are the people who share the row — and the policy behind
   * the channel is the same function the diagram's own policies call.
   * A read-only viewer joins too: an examiner reading over somebody's
   * shoulder is the case this is most useful for.
   */
  const { cursors, who, report } = useChartPresence({
    place: { dealId, interestEstimateId },
    profile,
  });

  // What is on the server, so a save can tell whether anything moved.
  // STATE rather than a ref: it is read during render to say whether
  // there are unsaved changes, and a ref changing does not re-render —
  // the status would keep saying "unsaved" after a save had landed.
  const [stored, setStored] = useState(null);
  const timer = useRef(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let live = true;
    (async () => {
      // Inside the async body, not beside it: a synchronous setState in
      // an effect forces an extra render before the load has even
      // started, which is the pattern every other loader here avoids.
      setLoading(true);
      setConflict(false);
      try {
        const out = await withTimeout(
          loadDoc
            ? loadDoc({ dealId, interestEstimateId, title })
            : loadDiagram(supabase, {
              dealId, interestEstimateId, title, userId: profile?.id,
            }),
          DIAGRAM_LOAD_TIMEOUT_MS,
          'The chart did not come back. Check your connection and try again.',
        );
        if (!live) return;
        if (out.error) {
          setError(errorMessage(out.error, 'The diagram could not be opened.'));
          return;
        }
        setRow({ id: out.data.id, version: out.data.version });

        // An empty diagram on a chain somebody has ALREADY drawn is the
        // one case that must not show a blank canvas: the old chart is
        // still in the database but nothing on screen would say so, and an
        // examiner who concludes the app lost their chain does not come
        // back to check. Carried automatically rather than behind a
        // button, because a button you do not know about is no help to
        // somebody looking at an empty screen.
        //
        // Both the carry and the seed below are conveniences, and a
        // convenience must never be what stops a chart opening — so each
        // is caught on its own and the canvas opens without it.
        let opened = out.data.doc;
        let carried = null;
        if (!opened.shapes.length && !opened.edges.length) {
          try {
            const old = await fetchLegacyChart(supabase, { dealId, interestEstimateId });
            if (!live) return;
            // null when there is no old chart (or it could not be read)
            // — it never returns an { error }. Reading .error off null
            // threw on every deal without a legacy flowchart, which was
            // caught and logged below, so the canvas opened and the
            // console said something had failed when nothing had.
            if (old) {
              const brought = documentFromChart(old);
              const said = legacySummary(brought);
              if (said) { opened = brought; carried = said; }
            }
          } catch (err) {
            console.warn('[SavedDiagram] carrying the old chart failed:', err);
          }
        }

        // STILL empty, and a runsheet to draw: build it.
        //
        // The button exists to protect an arrangement — a rebuild that
        // could undo an afternoon of layout is one nobody dares press,
        // which is how a chart goes stale. A chart with nothing on it has
        // no arrangement to protect, and a blank canvas on a job with
        // forty deeds on the runsheet reads as a broken tab rather than as
        // an invitation. Once anything is drawn this never runs again:
        // the chart is no longer empty.
        if (!opened.shapes.length && !opened.edges.length && (latestRows.current || []).length) {
          try {
            const seeded = seedFromRunsheet(opened, latestRows.current, { land });
            if (seeded.added.parties || seeded.added.deeds) {
              opened = seeded.doc;
              carried = seedSummary(seeded.added);
            }
          } catch (err) {
            console.warn('[SavedDiagram] seeding from the runsheet failed:', err);
          }
        }

        setDoc(opened);
        // Fit whatever arrived — including a chain just built from the
        // runsheet above, which is the case that needs it most: forty
        // deeds laid out wherever the builder put them, previously
        // opening at scale 1 on the world origin with the chart
        // somewhere off screen.
        if (opened.shapes.length) setFitKey((n) => (n || 0) + 1);
        // Deliberately NOT marked as stored: the carry is a change, so it
        // saves itself a moment later and the chain is on the canvas for
        // good rather than re-derived on every open.
        setStored(out.data.doc);
        setNotice(carried);
        setError(null);
      } catch (err) {
        // Whatever went wrong, the chart must not sit on "Loading"
        // forever. A spinner that never ends is the one failure nobody
        // can act on — no message, no retry, nothing to report.
        if (!live) return;
        console.error('[SavedDiagram] load failed:', err);
        setError(errorMessage(err, 'The diagram could not be opened.'));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [dealId, interestEstimateId, title, profile?.id, reloadToken, land, loadDoc]);

  const write = useCallback(async (next) => {
    if (!row) return;
    setSaving(true);
    const out = saveDoc
      ? await saveDoc({ id: row.id, doc: next, version: row.version })
      : await saveDiagram(supabase, { id: row.id, doc: next, version: row.version });
    setSaving(false);
    if (out.conflict) {
      // Stop. Another save would overwrite whatever they did.
      setConflict(true);
      return;
    }
    if (out.error) {
      setError(errorMessage(out.error, 'That could not be saved.'));
      return;
    }
    setStored(next);
    setRow((r) => ({ ...r, version: out.data.version }));
    setError(null);
    setSavedAt(new Date());
  }, [row, saveDoc]);

  // Debounced, and only when something actually differs from the server.
  useEffect(() => {
    if (loading || readOnly || conflict || !row) return undefined;
    // Never two at once. The version this would send is the one the last
    // save has not come back with yet, so on a slow connection a second
    // save overlapping the first is refused by its own predecessor — and
    // the screen tells somebody working alone that a colleague has
    // changed their chart, which is both untrue and alarming. The effect
    // re-runs when `saving` clears, so nothing is dropped.
    if (saving) return undefined;
    if (!docChanged(doc, stored)) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => write(doc), SAVE_AFTER_MS);
    return () => clearTimeout(timer.current);
  }, [doc, stored, loading, readOnly, conflict, row, saving, write]);

  // An unsaved change must not leave with the tab. This cannot save —
  // the browser gives no time — but it can stop somebody walking away
  // believing the work is filed.
  useEffect(() => {
    const onLeave = (event) => {
      if (readOnly || !docChanged(doc, stored)) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [doc, stored, readOnly]);

  const build = useCallback(() => {
    const out = seedFromRunsheet(doc, rows || [], { land });
    setDoc(out.doc);
    setNotice(seedSummary(out.added));
  }, [doc, rows, land]);


  /**
   * An imported chart is MERGED, never substituted.
   *
   * Its shapes arrive already re-identified — see LucidImport — so
   * importing the same page twice gives two independent copies rather
   * than two shapes sharing an id, which React resolves by dropping one
   * and which would leave the second import's arrows attached to the
   * first import's boxes.
   */
  const takeImport = useCallback((imported) => {
    setDoc((current) => ({
      shapes: [...current.shapes, ...imported.shapes],
      edges: [...current.edges, ...imported.edges],
      nextZ: Math.max(current.nextZ, imported.nextZ) + imported.shapes.length,
    }));
    setNotice(
      `Brought ${imported.shapes.length} shape${imported.shapes.length === 1 ? '' : 's'}`
      + ` and ${imported.edges.length} connector${imported.edges.length === 1 ? '' : 's'} across from Lucid.`,
    );
  }, []);

  /**
   * Open the recorded document a box was drawn from.
   *
   * In Shadowland Title this fetched the row itself and rendered its own
   * viewer. Here the host opens it: Paraclete's Documents card already
   * has the viewer, the signed-URL path and the document list in hand, so
   * a second copy inside the canvas would be a weaker one that drifts.
   *
   * A document that has since been deleted is worth saying out loud
   * rather than failing silently — the box is still on the chart, so
   * silence would read as a tag that does nothing. The host reports that
   * by resolving false.
   */
  const openDocument = useCallback(async (documentId, shape) => {
    if (!onOpenDocument) return;
    try {
      const found = await onOpenDocument(documentId, shape);
      if (found === false) {
        setNotice(
          `The document behind ${shape?.text ? `“${String(shape.text).split('\n')[0]}”` : 'that box'}`
          + ' is no longer in the file — it may have been deleted since the chart was drawn.',
        );
      }
    } catch (err) {
      setError(errorMessage(err, 'That document could not be opened.'));
    }
  }, [onOpenDocument]);

  if (loading) return <Spinner />;

  const dirty = docChanged(doc, stored);
  const status = conflict
    ? 'Not saved — somebody else has changed this'
    : saving ? 'Saving…'
      : dirty ? 'Unsaved changes'
        : savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : 'Saved';

  return (
    <>
      {error && (
        <Notice kind="error" onDismiss={() => setError(null)}>
          {error}
          {/* A chart that could not be opened at all has nothing to
              dismiss the message back to, so the way out is to try
              again rather than to close the window and wonder. */}
          {!row ? (
            <>
              {' '}
              <button
                type="button"
                className="btn-sm"
                data-diagram-retry
                onClick={() => { setError(null); setReloadToken((t) => t + 1); }}
              >
                Try again
              </button>
            </>
          ) : null}
        </Notice>
      )}
      {notice && <Notice onDismiss={() => setNotice(null)}>{notice}</Notice>}
      {conflict && (
        <Notice kind="warn">
          Somebody else has saved this diagram since you opened it. Reloading will show
          their version; anything you have drawn since will be lost.
          {' '}
          <button
            type="button"
            className="btn-sm"
            onClick={() => { setConflict(false); setReloadToken((t) => t + 1); }}
          >
            Reload theirs
          </button>
        </Notice>
      )}
      {importing && (
        <LucidImport
          doc={doc}
          onClose={() => setImporting(false)}
          onImport={(placed) => takeImport(placed)}
        />
      )}
      <DiagramEditor
        doc={doc}
        onChange={setDoc}
        readOnly={readOnly}
        onOpenDocument={openDocument}
        title={title}
        peers={cursors}
        onPointerReport={report}
        who={who}
        fitKey={fitKey}
        toolbar={(
          <>
            {/* Only where there is a runsheet behind the chain. A
                standalone assignment has none, and a button that can
                only ever say "nothing to add" is a button that teaches
                people the feature does not work. */}
            {!readOnly && rows?.length > 0 && (
              <button type="button" onClick={build} title="Draw every conveyance the runsheet holds">
                Build from runsheet
              </button>
            )}
            {!readOnly && (
              <button type="button" onClick={() => setImporting(true)} title="Bring a chart across from Lucid">
                From Lucid
              </button>
            )}
            <span className={`dge-status${conflict ? ' is-bad' : ''}${dirty && !conflict ? ' is-dirty' : ''}`}>
              {status}
            </span>
          </>
        )}
      />
    </>
  );
}
